<template>
    <div class="page-wrapper">
        <section data-section-type="HeroBanner" data-section-variant="default" data-section-theme="black">
            <article class="relative w-full h-[160px]">
                <!-- Insert Image Here -->
                <div class="container mx-auto px-6 py-6 xl:py-8 xl:px-4 flex flex-col items-start justify-center absolute inset-0 z-20 text-shadow">
                    <h1>Receipt</h1>
                    <h2 class="h4">Thank you for your purchase!</h2>
                </div>
            </article>
        </section>

        <section id="receipt" class="container mx-auto p-4 md:mb-4">
            <div class="flex flex-col md:flex-row-reverse gap-4">
                <div class="bg-leonard-grey rounded-md p-4 w-full md:w-1/3">
                    <h4 class="text-leonard-green pb-1 mb-1">Details</h4>

                    <div class="flex items-center justify-between md:justify-start lg:justify-between md:mb-2">
                        <strong>Date:</strong>
                        <div class="md:pl-1">{{ cart.completed_at }}</div>
                    </div>
                    <div class="flex items-center justify-between md:justify-start lg:justify-between md:mb-2">
                        <strong>Invoice #:</strong>
                        <div class="md:pl-1">{{ cart.invoice_number }}</div>
                    </div>
                    <div class="flex items-center justify-between md:justify-start lg:justify-between md:mb-2">
                        <strong>Order #:</strong>
                        <div class="md:pl-1">{{ cart.order_number }}</div>
                    </div>

                    <div class="flex flex-col mt-4">
                        <div
                            class="flex flex-col w-full"
                            v-if="cart.billing"
                        >
                            <h4 class="text-leonard-green pb-1 mb-1">Billing</h4>
                            <div class="flex flex-col mt-1">
                                <strong>Name:</strong>
                                <div>
                                    {{ cart.billing.first_name }}
                                    {{ cart.billing.last_name }}
                                </div>
                            </div>
                            <div class="flex flex-col mt-1">
                                <strong>Address:</strong>
                                <div>
                                    {{ cart.billing.address1 }}
                                    {{ cart.billing.address2 }}
                                </div>
                                <div>
                                    {{ cart.billing.city }},
                                    {{ cart.billing.state }}
                                    {{ cart.billing.zipcode }},
                                    {{ cart.billing.country }}
                                </div>
                            </div>
                            <div
                                class="flex flex-col mt-1"
                                v-if="cart.billing.phoneNumber"
                            >
                                <strong>Phone:</strong>
                                <div>
                                    {{
                                        cart.billing.phoneNumber
                                            | formatPhoneNumber
                                    }}
                                </div>
                            </div>
                        </div>

                        <div
                            class="flex flex-col w-full mt-3"
                            v-if="cart.shipping"
                        >
                            <h4 class="text-leonard-green pb-1 mb-1">Shipping</h4>
                            <div class="flex flex-col mt-1">
                                <strong>Name:</strong>
                                <div>
                                    {{ cart.shipping.first_name }}
                                    {{ cart.shipping.last_name }}
                                </div>
                            </div>
                            <div class="flex flex-col mt-1">
                                <strong>Address:</strong>
                                <div>
                                    {{ cart.shipping.address1 }}
                                    {{ cart.shipping.address2 }}
                                </div>
                                <div>
                                    {{ cart.shipping.city }},
                                    {{ cart.shipping.state }}
                                    {{ cart.shipping.zipcode }},
                                    {{ cart.shipping.country }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full md:w-2/3">
                    <p
                        v-if="transaction"
                        class="text-center text-sm mb-6 px-3"
                    >
                        Your order has been received and is in processing. You can access
                        this order from your <router-link :to="{ name: 'account' }" class="underline text-leonard-green hover:text-leonard-green-hover">profile</router-link>
                        at any time.
                    </p>

                    <Alert align="left" title="Notice" type="info" class="mt-0 mb-6">
                        All formal photos will be printed on the default traditional <a class="cursor-pointer underline text-blue-500" target="_blank" :href="image_url">JA background</a>.
                    </Alert>

                    <h4 class="pb-1 mb-3">Purchase Items</h4>

                    <div class="pb-6" v-for="item in items" :key="item.id">
                        <small class="block text-gray-500"
                            >{{ item.student.name }} -
                            {{ item.access_code }}</small
                        >
                        <div class="flex items-start justify-between">
                            <div>
                                <span class="font-semibold">{{
                                    item.name
                                }}</span>
                                <div
                                    class="flex"
                                    v-if="item.details.length > 0"
                                >
                                    <small class="text-leonard-green"
                                        >Contents:</small
                                    >
                                    <ul class="list-none text-sm">
                                        <li
                                            class="inline ml-1"
                                            v-for="(sheet, i) in item.details"
                                            :key="sheet.name"
                                        >
                                            {{ sheet.name }}
                                            <span
                                                v-if="
                                                    i + 1 < item.details.length
                                                "
                                                >,</span
                                            >
                                        </li>
                                    </ul>
                                </div>
                                <div class="flex" v-if="item.poses.length > 0">
                                    <small class="text-leonard-green">Poses:</small>
                                    <ul class="list-none text-sm">
                                        <li
                                            class="inline ml-1"
                                            v-for="(pose, i) in item.poses"
                                            :key="pose"
                                        >
                                            {{ pose }}
                                            <span
                                                v-if="i < item.poses.length - 1"
                                                >,</span
                                            >
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="text-right">
                                ${{ item.price | formatMoney }}
                            </div>
                        </div>
                        <div
                            class="flex items-center justify-between text-gray-500 text-sm"
                            v-for="sub in item.subItems"
                            :key="sub.id"
                        >
                            <div class="pl-4">{{ sub.name }}</div>
                            <div class="text-right">
                                ${{ sub.price | formatMoney }}
                            </div>
                        </div>
                        <div class="clearfix"></div>
                    </div>
                </div>
            </div>

            <div class="flex flex-col text-right mt-8">
                <div class="flex items-baseline justify-between md:justify-end w-full space-x-2">
                    <strong>Subtotal:</strong>
                    <span class="nowrap truncate">
                        ${{ cart.subtotal | formatMoney }}
                    </span>
                </div>
                <div
                    class="flex items-baseline justify-between md:justify-end w-full space-x-2 bg-green-100 border-t border-b border-green-500 text-green-800 my-2 py-3 text-lg"
                    v-if="parseFloat(cart.discount) > 0"
                >
                    <strong>
                        Discount Applied:
                        <span
                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-green-100 text-green-800 border border-green-500"
                        >{{ discount.code }}</span>
                    </strong>
                    <span class="nowrap truncate">
                        -${{ cart.discount | formatMoney }}
                    </span>
                </div>
                <div
                    class="flex items-baseline justify-between md:justify-end w-full space-x-2 bg-green-100 border-t border-b border-green-500 text-green-800 my-2 py-3 text-lg"
                    v-if="parseFloat(cart.voucher) > 0"
                >
                    <strong>Voucher Applied:</strong>
                    <span class="nowrap truncate">
                        -${{ cart.voucher | formatMoney }}
                    </span>
                </div>
                <div class="flex items-baseline justify-between md:justify-end w-full space-x-2">
                    <strong>Shipping &amp; Handling:</strong>
                    <span class="nowrap truncate">
                        ${{ cart.charged_shipping | formatMoney }}
                    </span>
                </div>
                <div class="flex items-baseline justify-between md:justify-end w-full space-x-2">
                    <strong>Tax:</strong>
                    <span class="nowrap truncate">
                        ${{ cart.charged_tax | formatMoney }}
                    </span>
                </div>
            </div>

            <div
                class="md:flex items-baseline justify-between mt-4 pt-4 text-2xl font-bold border-t-2 border-leonard-grey"
            >
                <div class="md:text-right">Total Billed Amount:</div>
                <div class="flex-grow md:text-right">
                    ${{ cart.charged_total | formatMoney }}
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import Alert from "../components/app/Alert";
import Cart from "../classes/Cart";
import Order from "../classes/Order";

export default {
    data() {
        return {
            Cart: new Cart(),
            Order: new Order(),
            transaction: this.$route.params.transaction,
            receiptDetails: {},
            cart_id: this.$route.params.cart_id,
            cart: {},
            discount: {},
            items: [],
            image_url: process.env.MIX_APP_URL + "/images/ja.jpg"
        };
    },

    created() {
        if (!this.cart_id || this.$route.params.length === "0") {
            this.$router.push({ name: "order" });
        }

        // Upon success, we want to show the user's cart items, so get those first
        this.getCart();
        window.events.$emit("refreshCart");
    },

    methods: {
        async getCart() {
            let self = this;
            let user_id = this.$auth.user().details.id;

            await this.Cart.getDetailedCart(user_id, {
                cart_id: this.cart_id
            }).then(function(response) {
                self.cart = response.data.cart;
                self.discount = response.data.discount;

                self.items = [];
                response.data.items.forEach(function(item) {
                    self.addItem(item);
                });
            }).catch(function(error) {
                console.error('Error fetching cart:', error);
            }).finally(async () => {
                if (this.cart && this.cart.order_number && this.cart.charged_total !== "0.00") {
                    await this.getReceiptDetails(this.cart.order_number);
                }

                window.events.$emit("finishedLoading");
            });
        },

        addItem(product) {
            let newItem = true;

            this.items.forEach(function(item) {
                if (product.parent_id === item.id) {
                    newItem = false;
                    item.subItems.push({
                        id: product.id,
                        pkg: product.package_number,
                        name: product.name,
                        price: product.price
                    });
                    item.subtotal =
                        parseFloat(item.price) + parseFloat(product.price);
                }
            });

            if (newItem) {
                this.items.push({
                    id: product.id,
                    student: product.student,
                    package: product.package_number,
                    name: product.name,
                    contents: product.contents,
                    price: product.price,
                    subtotal: product.price,
                    poses: product.poses,
                    details: product.details,
                    access_code: product.access_code,
                    subItems: []
                });
            }
        },
        async getReceiptDetails(order_number) {
            if (!order_number) {
                return
            }

            await this.Order.getReceipt({
                transactionId: order_number
            }).then((response) => {
                if (response.data) {
                    this.receiptDetails = response.data;
                }
            });
        }
    },

    filters: {
        formatPhoneNumber: function(phone) {
            return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
        },

        formatMoney(amount) {
            amount = parseFloat(amount);
            return amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
        }
    },

    components: {
        Alert
    }
};
</script>
